import { Badge } from '@/components/ui/badge';
import { EmptySkuIcon } from '../icons';
import { ImageCarousel } from '../common/ImageCarousel';
import type { Inventory } from '../Inventory/types';
import { useTranslation } from '@hooks/useTranslation';
import { formatCurrency } from '../../utils/formatters';

interface InventoryOverviewSectionProps {
  inventory: Inventory;
}

export function InventoryOverviewSection({ inventory }: InventoryOverviewSectionProps) {
  const { t } = useTranslation();

  return (
    <div>
      <h3 className="font-kapra-expanded text-2xl font-black uppercase leading-tight text-off-black mb-4">
        {t('common.overview.title')}
      </h3>

      <div className="flex gap-16 rounded-input border border-dinnerplate-white bg-off-white p-6">
        {/* Image carousel / placeholder */}
        <ImageCarousel
          images={(inventory.images ?? []).map((img) => ({ url: img.url }))}
          placeholder={<EmptySkuIcon className="w-24 h-20 text-grey" />}
          className="w-preview-image shrink-0"
        />

        {/* Details grid: 24px vertical gap, 48px horizontal gap */}
        <div className="flex flex-1 flex-col gap-8">
          {/* Name */}
          <div>
            <span className="font-inter text-sm font-semibold text-grey">{t('common.overview.name')}</span>
            <p className="font-inter text-sm font-semibold off-black mt-2">
              {inventory.name}
            </p>
          </div>

          {/* Category + Location */}
          <div className="grid grid-cols-2 gap-x-12">
            <div>
              <span className="font-inter text-sm font-semibold text-grey">{t('common.overview.category')}</span>
              <div className="mt-2">
                {inventory.category ? (
                  <Badge
                    variant="static"
                    className="font-inter w-fit border-0 rounded-xs bg-dinnerplate-white text-grey text-xs font-semibold px-1.5 py-1 leading-tight-sm"
                  >
                    {inventory.category}
                  </Badge>
                ) : (
                  <span className="font-inter text-sm text-grey">—</span>
                )}
              </div>
            </div>
            <div>
              <span className="font-inter text-sm font-semibold text-grey">{t('common.overview.location')}</span>
              <p className="font-inter text-sm text-off-black mt-2">
                {inventory.location || '—'}
              </p>
            </div>
          </div>

          {/* Retail price + Cost price */}
          <div className="grid grid-cols-2 gap-x-12">
            <div>
              <span className="font-inter text-sm font-semibold text-grey">{t('common.overview.retail_price')}</span>
              <p className="font-inter text-sm text-off-black mt-2">
                {formatCurrency(inventory.recommended_retail_price)}
              </p>
            </div>
            <div>
              <span className="font-inter text-sm font-semibold text-grey">{t('common.overview.cost_price')}</span>
              <p className="font-inter text-sm text-off-black mt-2">
                {formatCurrency(inventory.cost_to_company)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
